const MS_MIN = 60_000;
const MS_DAY = 86_400_000;
/** Beyond this span, show a calendar-style label instead of “il y a N jours”. */
const FAR_MS = 60 * MS_DAY;

/** Dim … Sam — short French weekday for compact admin timestamps */
const FR_WEEKDAY_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'] as const;

function detailedTimestamp(iso: Date): { detail: string; detailDate: string; detailTime: string } {
  const wd = FR_WEEKDAY_SHORT[iso.getDay()];
  const datePart = iso.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timePart = iso.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const detailDate = `${wd} ${datePart}`;
  const detailTime = `à ${timePart}`;
  return { detail: `${detailDate} ${detailTime}`, detailDate, detailTime };
}

/**
 * French-friendly relative label + full datetime for tooltips (locale = fr-FR, browser TZ).
 */
export function formatFriendlyDate(
  iso: string | null | undefined,
  now = new Date(),
): { text: string; detail: string; detailDate: string; detailTime: string } {
  if (iso == null || iso === '')
    return { text: '—', detail: '', detailDate: '', detailTime: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()))
    return { text: String(iso), detail: String(iso), detailDate: String(iso), detailTime: '' };

  const { detail, detailDate, detailTime } = detailedTimestamp(d);
  const diffMs = now.getTime() - d.getTime();
  const abs = Math.abs(diffMs);
  const future = diffMs < 0;

  if (abs > FAR_MS) {
    return {
      text: d.toLocaleDateString('fr-FR', { dateStyle: 'medium' }),
      detail,
      detailDate,
      detailTime,
    };
  }

  const rtf = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });

  if (future) {
    const sec = Math.floor(abs / 1000);
    if (sec < 60) return { text: 'À venir', detail, detailDate, detailTime };
    const minutes = Math.round(abs / MS_MIN);
    if (minutes < 60) return { text: rtf.format(minutes, 'minute'), detail, detailDate, detailTime };
    const hours = Math.floor(abs / 3_600_000);
    if (hours < 24) return { text: rtf.format(hours, 'hour'), detail, detailDate, detailTime };
    const days = Math.ceil(abs / MS_DAY);
    if (days < 7) return { text: rtf.format(days, 'day'), detail, detailDate, detailTime };
    return {
      text: d.toLocaleDateString('fr-FR', { dateStyle: 'medium' }),
      detail,
      detailDate,
      detailTime,
    };
  }

  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return { text: 'À l’instant', detail, detailDate, detailTime };
  const minutes = Math.floor(diffMs / MS_MIN);
  if (minutes < 1) return { text: 'À l’instant', detail, detailDate, detailTime };
  if (minutes < 60) return { text: rtf.format(-minutes, 'minute'), detail, detailDate, detailTime };
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return { text: rtf.format(-hours, 'hour'), detail, detailDate, detailTime };
  const days = Math.floor(diffMs / MS_DAY);
  if (days < 7) return { text: rtf.format(-days, 'day'), detail, detailDate, detailTime };
  return {
    text: d.toLocaleDateString('fr-FR', { dateStyle: 'medium' }),
    detail,
    detailDate,
    detailTime,
  };
}

/** Chat-style relative label: `21min`, `2h`, `3j` (tooltip still full datetime). */
export function formatCompactFriendlyDate(
  iso: string | null | undefined,
  now = new Date(),
): { text: string; detail: string; detailDate: string; detailTime: string } {
  if (iso == null || iso === '')
    return { text: '—', detail: '', detailDate: '', detailTime: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()))
    return { text: String(iso), detail: String(iso), detailDate: String(iso), detailTime: '' };

  const { detail, detailDate, detailTime } = detailedTimestamp(d);
  const diffMs = now.getTime() - d.getTime();
  const abs = Math.abs(diffMs);
  const future = diffMs < 0;

  if (abs > FAR_MS) {
    return {
      text: d.toLocaleDateString('fr-FR', { dateStyle: 'medium' }),
      detail,
      detailDate,
      detailTime,
    };
  }

  if (future) {
    const minutes = Math.round(abs / MS_MIN);
    if (minutes < 1) return { text: 'bientôt', detail, detailDate, detailTime };
    if (minutes < 60) return { text: `dans ${minutes}min`, detail, detailDate, detailTime };
    const hours = Math.floor(abs / 3_600_000);
    if (hours < 24) return { text: `dans ${hours}h`, detail, detailDate, detailTime };
    const days = Math.ceil(abs / MS_DAY);
    if (days < 7) return { text: `dans ${days}j`, detail, detailDate, detailTime };
    return {
      text: d.toLocaleDateString('fr-FR', { dateStyle: 'medium' }),
      detail,
      detailDate,
      detailTime,
    };
  }

  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return { text: 'maintenant', detail, detailDate, detailTime };
  const minutes = Math.floor(diffMs / MS_MIN);
  if (minutes < 60) return { text: `${minutes}min`, detail, detailDate, detailTime };
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return { text: `${hours}h`, detail, detailDate, detailTime };
  const days = Math.floor(diffMs / MS_DAY);
  if (days < 7) return { text: `${days}j`, detail, detailDate, detailTime };
  return {
    text: d.toLocaleDateString('fr-FR', { dateStyle: 'medium' }),
    detail,
    detailDate,
    detailTime,
  };
}

/**
 * Expiry-style countdown, French, multi-unit.
 * Example: « dans 14 jours, 12 heures, 20 minutes ».
 * Keeps full datetime in `detail` for tooltips.
 */
export function formatCountdownFriendlyFr(
  iso: string | null | undefined,
  now = new Date(),
): { text: string; detail: string } {
  if (iso == null || iso === '') return { text: '—', detail: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { text: String(iso), detail: String(iso) };

  const { detail } = detailedTimestamp(d);
  const diffMs = d.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const future = diffMs > 0;

  const MS_HOUR = 3_600_000;
  const MS_SEC = 1_000;
  const MS_MIN_LOCAL = 60_000;

  const parts: string[] = [];
  const add = (n: number, singular: string, plural: string) => {
    if (n <= 0) return;
    parts.push(n === 1 ? `1 ${singular}` : `${n} ${plural}`);
  };

  if (abs < MS_MIN_LOCAL) {
    const sec = Math.max(1, Math.round(abs / MS_SEC));
    add(sec, 'seconde', 'secondes');
  } else {
    const days = Math.floor(abs / MS_DAY);
    if (days > 30) {
      const a = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
      const b = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
      const { y, m, d: dd } = calendarDiffYMD(a, b);
      const totalMonths = y * 12 + m;
      add(totalMonths, 'mois', 'mois');
      add(dd, 'jour', 'jours');
    } else {
      const hours = Math.floor((abs - days * MS_DAY) / MS_HOUR);
      const minutes = Math.floor((abs - days * MS_DAY - hours * MS_HOUR) / MS_MIN_LOCAL);
      add(days, 'jour', 'jours');
      add(hours, 'heure', 'heures');
      // keep minutes as last unit, even if 0 when we already show days/hours (avoids “dans 2 jours” ambiguity)
      if (minutes > 0 || parts.length > 0) add(minutes, 'minute', 'minutes');
    }
  }

  const trimmed = parts.slice(0, 3).join(', ');
  if (!trimmed) return { text: future ? 'À venir' : 'Expiré', detail };
  return {
    text: future ? `dans ${trimmed}` : `expiré il y a ${trimmed}`,
    detail,
  };
}

function pad2(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, '0');
}

/**
 * Live expiry clock — days + hh/mm/ss so a 1s tick is visible.
 * Example: « dans 713 j 04 h 12 min 08 s ».
 */
export function formatCountdownLiveFr(
  iso: string | null | undefined,
  now = new Date(),
): { text: string; detail: string; remainingMs: number } {
  if (iso == null || iso === '') return { text: '—', detail: '', remainingMs: 0 };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { text: String(iso), detail: String(iso), remainingMs: 0 };

  const { detail } = detailedTimestamp(d);
  const remainingMs = d.getTime() - now.getTime();
  const future = remainingMs > 0;
  const abs = Math.abs(remainingMs);
  const secTotal = Math.floor(abs / 1000);
  if (!future && secTotal === 0) return { text: 'Expiré', detail, remainingMs };

  const days = Math.floor(secTotal / 86_400);
  const hours = Math.floor((secTotal % 86_400) / 3_600);
  const minutes = Math.floor((secTotal % 3_600) / 60);
  const seconds = secTotal % 60;
  const clock = `${days} j ${pad2(hours)} h ${pad2(minutes)} min ${pad2(seconds)} s`;
  return {
    text: future ? `dans ${clock}` : `expiré il y a ${clock}`,
    detail,
    remainingMs,
  };
}

/**
 * Format `YYYY-MM-DD` (local calendar day) for labels — avoids UTC midnight skew from `Date.parse`.
 * Long French date (e.g. « 16 mai 2027 »).
 */
export function formatIsoDateOnlyFr(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const t = value.trim();
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return t;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return t;
  return dt.toLocaleDateString('fr-FR', { dateStyle: 'long' });
}

function calendarDiffYMD(from: Date, to: Date): { y: number; m: number; d: number } {
  if (to.getTime() < from.getTime()) return calendarDiffYMD(to, from);
  let y = to.getFullYear() - from.getFullYear();
  let m = to.getMonth() - from.getMonth();
  let d = to.getDate() - from.getDate();
  if (d < 0) {
    m -= 1;
    d += new Date(to.getFullYear(), to.getMonth(), 0).getDate();
  }
  if (m < 0) {
    y -= 1;
    m += 12;
  }
  return { y, m, d };
}

/**
 * Expiry date (`YYYY-MM-DD`) shown as French relative text from “today”
 * (e.g. « dans 2 ans », « dans 3 mois et 5 jours », « il y a 2 semaines »).
 * Tooltip should still use {@link formatIsoDateOnlyFr} for the fixed calendar form.
 */
export function formatExpiryRelativeFr(value: string | null | undefined, now = new Date()): string {
  if (value == null || value === '') return '—';
  const t = value.trim();
  const match = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return t;
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const exp = new Date(y, mo - 1, d, 12, 0, 0);
  if (Number.isNaN(exp.getTime())) return t;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
  const ms = exp.getTime() - today.getTime();
  const rtf = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
  const absDays = Math.round(Math.abs(ms) / MS_DAY);

  if (ms === 0) return "Aujourd'hui";

  if (ms > 0) {
    if (absDays < 7) return rtf.format(absDays, 'day');
    if (absDays < 60) return rtf.format(Math.max(1, Math.round(absDays / 7)), 'week');

    const { y: cy, m: cm, d: cd } = calendarDiffYMD(today, exp);
    const parts: string[] = [];
    if (cy > 0) parts.push(cy === 1 ? '1 an' : `${cy} ans`);
    if (cm > 0) parts.push(cm === 1 ? '1 mois' : `${cm} mois`);
    if (cd > 0) parts.push(cd === 1 ? '1 jour' : `${cd} jours`);
    if (parts.length > 0) return `dans ${parts.join(' et ')}`;
    return rtf.format(absDays, 'day');
  }

  if (absDays < 7) return rtf.format(-absDays, 'day');
  if (absDays < 60) return rtf.format(-Math.max(1, Math.round(absDays / 7)), 'week');

  const { y: cy, m: cm, d: cd } = calendarDiffYMD(exp, today);
  const parts: string[] = [];
  if (cy > 0) parts.push(cy === 1 ? '1 an' : `${cy} ans`);
  if (cm > 0) parts.push(cm === 1 ? '1 mois' : `${cm} mois`);
  if (cd > 0) parts.push(cd === 1 ? '1 jour' : `${cd} jours`);
  if (parts.length > 0) return `il y a ${parts.join(' et ')}`;
  return rtf.format(-absDays, 'day');
}
